import { Type, Static, TAnySchema } from '@sinclair/typebox'

import { CattlePurchasesTB, ConsumablesRecordTB, Pasture_PreV3_5TB, SheepPurchasesRecordTB, SupplementaryFeedTB, MineralSupplementRecordTB, TreesTB, Pasture_PostV3_4RecordTB } from './report_shared'
import { CattleTB, SheepTB, ProductionFarmRecordTB, LambingAndWoolRecordTB, CalvingRecordTB, ProducerGrainsPreV3_4RecordTB, ProducerGrainsPostV3_3RecordTB, LivestockRecordTB } from './report_farm'
import { DairyCattleTB, DairyManureManagementRecordTB, DairyProductionSystemRecordTB, DairyCattleTransportTB, DairyProductionTB } from './report_dairy'
import { FeedlotRationsRecordTB, FeedlotCohortsTB, FeedlotCattlePurchasesTB, FeedlotProductionTB } from './report_feedlot'
import { ProcessorConsumablesRecordTB, ProcessorEffluentManagementRecordTB, ProcessorMethaneRecordTB, ProcessorProductionTB, ProcessorRefrigerantsTB, ProcessorWaterUseTB, ProcessorConsumablesFormTB } from './report_processor'
import { FurtherProcessorPurchasedGoodsTB } from './report_further_processor'
import { AllocatedResultsTB, ReportResultsTB } from './report_results'

export type ReportBase = {
  id: string
  propertyId: string
  financialYear: number
  algorithmVersion: number
  createdDate: number

  complete: boolean
  resultsReadyForGeneration: boolean

  // TO DO: Improve this so maybe it's a generic?
  completedSections: string[]
  completionDate: null | number

  // TO DO: Add support for results
  // results?: ReportResults
}

export const ReportTB = Type.Object({
  id: Type.String(), // uuid
  uid: Type.String(), // user.id
  propertyId: Type.String(), // uuid
  algorithmVersion: Type.Number(),
  createdDate: Type.Number(), // timestamp TODO: should this be a Date like other createdDates in entities?
  deletedAt: Type.Optional(Type.Date()), // TODO: not nullable in db, but what type is returned?
  financialYear: Type.Number(),
  complete: Type.Boolean(),
  completionDate: Type.Optional(Type.Number()), // timestamp TODO: should this be a Date like other createdDates in entities? N.B. entity def allows for null, but optional rather than nullable in application code
  completedSections: Type.Array(Type.String()), // Set

  results: Type.Optional(ReportResultsTB),
  resultsByProduct: Type.Optional(AllocatedResultsTB)
})
export type ReportT = Static<typeof ReportTB>

export const ReportCreateTB = Type.Pick(ReportTB, [
  'propertyId',
  'financialYear'
])
export type ReportCreateT = Static<typeof ReportCreateTB>

// TODO: leaving for the moment to consider whether ReportSummaryTB should have a "summary" property bolted on for easier filtering?
export const ReportSummaryTB = Type.Composite([
  ReportTB,
  Type.Object({
    summary: Type.Literal(true)
  })
])
// TODO: for now, report summary is the same as report above (as report above doesn't include actual input information)
// export const ReportSummaryTB = ReportTB
export type ReportSummaryT = Static<typeof ReportSummaryTB>

// TODO: this should probably just be a Partial, right? There isn't a useful data structure that has every possible report input?
// _and_ - should ReportInputsTB be a union of each of the forms? does that make sense / easier to manage?
// TODO: and what about algorithm versioning - in theory, each version would have a different set of inputs?
export const ReportInputsTB = Type.Object({
  // shared
  consumablesInformation: Type.Union([ConsumablesRecordTB, ProcessorConsumablesFormTB]),
  // TODO: should probably have a single key for same data but different versions?
  pastureInformation: Pasture_PreV3_5TB,
  pastureInformationPostV3_4: Pasture_PostV3_4RecordTB,
  cattlePurchasesInformation: Type.Union([CattlePurchasesTB, FeedlotCattlePurchasesTB]),
  sheepPurchasesInformation: SheepPurchasesRecordTB,
  mineralSupplementsInformation: MineralSupplementRecordTB,
  supplementaryFeedInformation: SupplementaryFeedTB,
  livestockInformation: LivestockRecordTB,
  livestockInformationCattleClassAus: CattleTB,
  livestockInformationCattleMiscAus: CalvingRecordTB,
  livestockInformationSheepClassAus: SheepTB,
  livestockInformationSheepMiscAus: LambingAndWoolRecordTB,

  // TODO: should probably have a single key for same data but different versions?
  grainInformation: ProducerGrainsPreV3_4RecordTB,
  grainInformationPostV3_3: ProducerGrainsPostV3_3RecordTB,
  treesInformation: TreesTB,
  productionInformation: ProductionFarmRecordTB,

  // dairy
  dairyCattleClassInformation: DairyCattleTB,
  dairyManureManagementInformation: DairyManureManagementRecordTB,
  dairyProductionSystemInputsInformation: DairyProductionSystemRecordTB,
  dairyTransportInformation: DairyCattleTransportTB,
  dairyProductionInformation: DairyProductionTB,

  // feedlot
  rationsInformation: Type.Array(FeedlotRationsRecordTB),
  feedlotCohortsInformation: FeedlotCohortsTB,
  feedlotProductionInformation: FeedlotProductionTB,

  // processor
  processorConsumablesInformation: ProcessorConsumablesRecordTB,
  methaneInformation: ProcessorMethaneRecordTB,
  refrigerantsInformation: ProcessorRefrigerantsTB,
  effluentManagementInformation: ProcessorEffluentManagementRecordTB,
  processorWaterUseInformation: ProcessorWaterUseTB,
  processorProductionInformation: ProcessorProductionTB,

  // further processor
  purchasedGoodsTonnesCO2EInformation: FurtherProcessorPurchasedGoodsTB
})
export type ReportInputsT = Static<typeof ReportInputsTB>

export const ReportInputsPartialTB = Type.Partial(ReportInputsTB)
export type ReportInputsPartialT = Static<typeof ReportInputsPartialTB>

export const ReportWithInputsTB = Type.Composite([
  ReportTB,
  ReportInputsTB
])
export type ReportWithInputsT = Static<typeof ReportWithInputsTB>

export const ReportWithPartialInputsTB = Type.Composite([
  ReportTB,
  ReportInputsPartialTB
])
export type ReportWithPartialInputsT = Static<typeof ReportWithPartialInputsTB>

// TODO: these should possibly belong in report_shared, more about forms?
export const ReportErrorTypeErrorTB = Type.Literal('error')
export const ReportErrorTypeWarningTB = Type.Literal('warning')

export const ReportErrorTypeTB = Type.Union([
  ReportErrorTypeErrorTB,
  ReportErrorTypeWarningTB
])
export type ReportErrorTypeT = Static<typeof ReportErrorTypeTB>

export const ReportErrorTB = Type.Object({
  type: ReportErrorTypeTB,
  message: Type.String()
})
export type ReportErrorT = Static<typeof ReportErrorTB>

export const VisionReportSummaryTB = Type.Object({
  propertyInformation: Type.Object({
    id: Type.String()
  }),
  reportInformation: Type.Object({
    id: Type.String(),
    year: Type.Number()
  }),
  summaryResults: ReportResultsTB
})
export type VisionReportSummaryT = Static<typeof VisionReportSummaryTB>


export type ValidationResult = { path: string, errors: Array<ReportErrorT> }
export type ValidationResults = Array<ValidationResult>
export type AdditionalValidators<_T extends TAnySchema> = Array<{
  match: (path: string) => boolean,
  validators: Array<(formState: unknown, matchedPath: string) => ValidationResults>
}>
